import React, { createContext, useState, useMemo, useCallback, useContext } from 'react';
import { useOverlayTriggerState } from 'react-stately';
import { useNavContext } from '../nav/Nav.js';
import { getProductUrl } from './productUrl.js';

// This dummy ID and name match to the blank customer that the graph can return
const FALLBACK_CUSTOMER = {
    id: "0",
    name: ""
};
const ExperienceSwitcherContext = createContext(undefined);
const ExperienceSwitcherProvider = (props)=>{
    const navContext = useNavContext();
    const modalState = useOverlayTriggerState({});
    const [activeCustomerId, setActiveCustomerId] = useState();
    const [customers, initialActiveCustomerId] = useMemo(()=>{
        const customers = {};
        let initialActiveCustomerId;
        if (navContext.currentUser) {
            navContext.currentUser?.businessMemberships.edges.forEach((b)=>{
                const customer = b.node.business.customer || FALLBACK_CUSTOMER;
                if (customers[customer.id] === undefined) {
                    // Add this customer to our map of customers
                    customers[customer.id] = {
                        id: customer.id,
                        name: customer.name,
                        productLines: {}
                    };
                }
                if (navContext.businessId === b.node.business.id) {
                    initialActiveCustomerId = customer.id;
                }
                // Add the products for this business to the customer definition
                b.node.business.products?.edges.forEach((p)=>{
                    const product = p.node;
                    if (customers[customer.id].productLines[product.line] === undefined) {
                        customers[customer.id].productLines[product.line] = {
                            products: {}
                        };
                    }
                    //  Add the product environment to the product definition
                    if (customers[customer.id].productLines[product.line].products[product.name] === undefined) {
                        customers[customer.id].productLines[product.line].products[product.name] = {
                            name: product.name,
                            beta: product.beta,
                            productEnvironments: []
                        };
                    }
                    customers[customer.id].productLines[product.line].products[product.name].productEnvironments.push({
                        businessId: b.node.business.id,
                        link: getProductUrl(product.name, b.node.business.id, navContext.env),
                        name: b.node.business.name,
                        active: b.node.business.id === navContext.businessId && product.name === navContext.activeProduct
                    });
                });
            });
        }
        return [
            customers,
            initialActiveCustomerId
        ];
    }, [
        navContext.currentUser,
        navContext.businessId,
        navContext.env,
        navContext.activeProduct
    ]);
    const sortedCustomers = useMemo(()=>{
        return Object.values(customers).sort((a, b)=>{
            return a.name.localeCompare(b.name);
        });
    }, [
        customers
    ]);
    // Helper function used to reinitilize the switcher state
    const resetSwitcher = useCallback(()=>{
        setActiveCustomerId(undefined);
    }, []);
    const value = useMemo(()=>{
        return {
            activeCustomer: activeCustomerId ? customers[activeCustomerId] : customers[initialActiveCustomerId] ?? // If no businessId was provided, just pick the first customer to display
            sortedCustomers[0],
            customers: sortedCustomers,
            modalState,
            resetSwitcher,
            setActiveCustomerId
        };
    }, [
        activeCustomerId,
        customers,
        initialActiveCustomerId,
        sortedCustomers,
        modalState,
        resetSwitcher
    ]);
    return React.createElement(ExperienceSwitcherContext.Provider, {
        value: value
    }, props.children);
};
const useExperienceSwitcher = ()=>{
    const uxsContext = useContext(ExperienceSwitcherContext);
    return uxsContext;
};

export { ExperienceSwitcherProvider, useExperienceSwitcher };
